export type Role = 'admin' | 'practitioner' | 'client';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  /** True when this user also has a practitioner profile (e.g. Liev = admin + practitioner). */
  isPractitioner: boolean;
  /** The user's own Practitioner row id, or null. */
  practitionerId: string | null;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  practitioner: 'Practitioner',
  client: 'Client',
};

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  isPractitioner: boolean;
  createdAt: string;
}

/** Workflow status a practitioner sets by hand. Invite state is separate — see InviteState. */
export type ClientStatus = 'active' | 'on_hold' | 'archived';

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  active: 'Active',
  on_hold: 'On hold',
  archived: 'Archived',
};

/**
 * Whether the client can log in yet. Derived server-side from their password and
 * any open invitation — never stored, so it can't drift from reality.
 */
export type InviteState = 'accepted' | 'pending' | 'expired';

export const INVITE_LABELS: Record<InviteState, string> = {
  accepted: 'Active',
  pending: 'Invited',
  expired: 'Invite expired',
};

/** Goals the API accepts, mirroring CLIENT_GOALS in server/src/lib/constants.ts. */
export const CLIENT_GOALS = ['Sleep', 'Study', 'Confidence', 'Fitness', 'Intimacy', 'Smoking'] as const;

export interface ClientListItem {
  /** The User id — a client is a user, and ClientProfile is 1:1 detail on it. */
  id: string;
  name: string;
  email: string;
  goal: string | null;
  program: string | null;
  status: ClientStatus;
  inviteState: InviteState;
  inviteExpiresAt: string | null;
  practitionerId: string | null;
  practitionerName: string | null;
  createdAt: string;
}

export interface ClientDetail extends ClientListItem {
  intakeNotes: string | null;
  updatedAt: string;
  /** The assigned practitioner's Cal.com path, for the book-for-client embed. */
  practitionerCalLink: string | null;
}

export interface PractitionerOption {
  id: string;
  name: string;
}

export type SessionStatus = 'pending_payment' | 'confirmed' | 'cancelled' | 'completed';

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  pending_payment: 'Awaiting payment',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export interface SessionItem {
  id: string;
  title: string | null;
  startsAt: string; // ISO UTC
  endsAt: string | null;
  status: SessionStatus;
  videoUrl: string | null;
  cancelUrl: string | null;
  rescheduleUrl: string | null;
  client: { id: string; name: string } | null;
  /** Fallback identity when no portal client is linked yet. */
  attendeeName: string | null;
  amountCents: number | null;
  currency: string;
}

export type AudioKind = 'audible' | 'ultrasonic';

export const AUDIO_KIND_LABELS: Record<AudioKind, string> = {
  audible: 'Audible',
  ultrasonic: 'Ultrasonic',
};

/**
 * One shape for both roles (SessionItem nullable-precedent): the server sends
 * manager fields (published, listeners, wooProductId…) to practitioners/admins
 * and client fields (entitled) to clients — the role already discriminates.
 */
export interface AudioItem {
  id: string;
  title: string;
  description: string | null;
  kind: AudioKind;
  format: string;
  durationSec: number | null;
  sizeBytes: number | null;
  storeUrl: string | null;
  /** Client-role responses. */
  entitled?: boolean;
  /** Manager-role responses. */
  published?: boolean;
  listeners?: number;
  originalName?: string | null;
  wooProductId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Present on GET /api/audio?clientId= rows. */
  source?: AccessGrantSource | null;
  grantedAt?: string | null;
}

export type AccessGrantSource = 'woo' | 'grant' | 'purchase' | 'subscription';

export const GRANT_SOURCE_LABELS: Record<AccessGrantSource, string> = {
  woo: 'Store',
  grant: 'Manual',
  purchase: 'Purchase',
  subscription: 'Subscription',
};

/** POST /api/invites/validate — a union rather than an error, so the page can render all three. */
export type InviteValidation =
  | { valid: true; email: string; name: string }
  | { valid: false; reason: 'invalid' | 'expired' | 'used' };
